<template>
<global-modal v-model="isOpened" width="850px">
    <special-wish-form
        v-if="isOpened && enhancedTexts"
        ref="specialWishForm"
        :texts="enhancedTexts"
        :saved-url="savedUrl"
        :succeeded="succeeded"
        :prefill-email="props.prefillEmail"
        :is-logged-in="props.isLoggedIn"
        :allow-attachments="props.allowAttachments ?? false"
        @submit="submit"
    />
</global-modal>
</template>

<script setup lang="ts">
import {SpecialWishForm, GlobalModal} from '@webstacz/shop-components';
import type {SpecialWishFormTextsType} from '@webstacz/shop-components/dist/types/src/types/modals/SpecialWishFormTextsType';
import {Reactive, reactive, ref, computed, useTemplateRef, watch} from "vue";
import {useI18n} from "vue-i18n";

export type Props = {
    texts?: SpecialWishFormTextsType;
    prefillEmail?: string;
    isLoggedIn?: boolean;
    allowAttachments?: boolean;
};

const props: Reactive<Props> = reactive({})
const {t, te} = useI18n();

const specialWishFormRef = useTemplateRef<typeof SpecialWishForm>('specialWishForm');

const isOpened = ref(false);
const succeeded = ref(false);
const savedUrl = ref('');
let submitCallback: Parameters<typeof window.openSpecialWishModal>[1] = () => Promise.resolve();

const endOfThisMonth = computed((): string => {
    const today = new Date();
    const date = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return `${date.getDate()}.${date.getMonth() + 1}.${date.getFullYear()}`;
});

const tr = (key: string, fallback = '', vars: Record<string, string> = {}): string =>
    (te(key) ? t(key, vars) : fallback);

const enhancedTexts = computed((): SpecialWishFormTextsType | undefined => {
    if (!props.texts) {
        return undefined;
    }
    return {
        ...props.texts,
        message: {
            ...props.texts.message,
            prefill: props.texts.message?.prefill || tr('special-wish-modal-prefill'),
        },
        success: {
            title: props.texts.success?.title || tr('special-wish-modal-success-title'),
            info: props.texts.success?.info || tr('special-wish-modal-success-info', '', {
                endOfThisMonth: endOfThisMonth.value,
            }),
            urlText: props.texts.success?.urlText
                || props.texts.success?.url_text
                || tr('special-wish-modal-success-urlText'),
            whatsAppText: props.texts.success?.whatsAppText || tr('share-now-whatsapp'),
        },
        attachments: props.texts.attachments ?? {
            link: tr('special-wish-modal-attachment-link'),
            add: tr('attachment-upload-add'),
            allowedFiles: tr('attachment-upload-allowed-files'),
            remove: tr('attachment-upload-remove'),
            maxFilesError: tr('attachment-upload-error-max-files'),
            maxSizeError: tr('attachment-upload-error-max-size'),
            typeError: tr('attachment-upload-error-type'),
        },
    };
});

watch(isOpened, () => {
    if (specialWishFormRef.value) {
        specialWishFormRef.value.resetForm();
    }
    succeeded.value = false;
    savedUrl.value = '';
});

window.openSpecialWishModal = (config, submitCb): void => {
    Object.assign(props, config);
    if (window.customerAuth?.isAuthenticated && window.customerAuth.email) {
        props.prefillEmail = window.customerAuth.email;
        props.isLoggedIn = true;
    } else {
        props.isLoggedIn = false;
    }
    submitCallback = submitCb;
    isOpened.value = true;
};

const submit = async (form: any) => {
    try {
        const result = await submitCallback(form);
        if (typeof result === 'string') {
            savedUrl.value = result;
        }
        succeeded.value = true;
    } catch (e) {
        console.error(e);
        specialWishFormRef.value?.showError(tr('attachment-upload-error-send'));
    } finally {
        specialWishFormRef.value?.clearSending();
    }
}
</script>

<style module lang="scss">

</style>
